import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { doc, getDoc } from 'firebase/firestore';
import { validateSignup } from '../../../utils/reduxFormUtils';
import { validateAndSignUpUser } from '../actions';
import { Button, ButtonTypes } from '../../../common/Button';
import { db } from '../../../firebase/init';
import {
  FormLike,
  useSyncFormTranslations
} from '../../../common/useSyncFormTranslations';
import { CreateUserRequestBody } from '../../../../common/types';

let usernamePending: {
  timer: ReturnType<typeof setTimeout>;
  resolve: (msg: string) => void;
} | null = null;

function validateUsername(
  username: CreateUserRequestBody['username']
): Promise<string> {
  if (!username || username.trim().length === 0) return Promise.resolve('');
  return new Promise((resolve) => {
    // Supersede the previous debounced check — and RESOLVE its promise, otherwise react-final-form
    // is left awaiting a never-settling validation and the form stays stuck in `validating`.
    if (usernamePending) {
      clearTimeout(usernamePending.timer);
      usernamePending.resolve('');
    }
    const timer = setTimeout(async () => {
      usernamePending = null;
      try {
        const snap = await getDoc(doc(db, `usernames/${username}`));
        resolve(snap.exists() ? 'That username is already taken.' : '');
      } catch {
        resolve('');
      }
    }, 300);
    usernamePending = { timer, resolve };
  });
}

export function SignupForm({
  onCreatingChange
}: {
  // Notifies the parent when account creation starts/stops, so it can hide the page title and the
  // navigation links while the "Creating account…" message is shown in place of the form.
  onCreatingChange?: (creating: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const formRef = useRef<FormLike<CreateUserRequestBody> | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useSyncFormTranslations(formRef, i18n.language);

  const handleVisibility = () => setShowPassword(!showPassword);
  const handleConfirmVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  function onSubmit(formProps: CreateUserRequestBody) {
    onCreatingChange?.(true);
    // The thunk resolves to a `{ [FORM_ERROR]: ... }` object on error, or undefined on success (it
    // then redirects to /login, unmounting this view). Reveal the form again only when it failed.
    const submitResult = (dispatch(
      validateAndSignUpUser(formProps)
    ) as unknown) as Promise<Record<string, string> | undefined>;
    return submitResult.then((result) => {
      if (result) onCreatingChange?.(false);
      return result;
    });
  }

  return (
    <Form
      fields={['username', 'email', 'password', 'confirmPassword']}
      validate={validateSignup}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        pristine,
        submitting,
        invalid,
        submitError,
        modifiedSinceLastSubmit,
        form
      }) => {
        formRef.current = form;
        // While the account is being created (and the verification email sent), replace the whole
        // form with a clear status message so the user knows something is happening — the request can
        // take a few seconds before we redirect to /login.
        if (submitting) {
          return (
            <div className="form__creating" role="status" aria-live="polite">
              {t('SignupForm.Creating')}
            </div>
          );
        }
        return (
          <form className="form" onSubmit={handleSubmit}>
            <Field
              name="username"
              validate={validateUsername}
              validateFields={[]}
            >
              {(field) => (
                <div className="form__field">
                  <label htmlFor="username" className="form__label">
                    {t('SignupForm.Title')}
                  </label>
                  <input
                    className="form__input"
                    aria-label={t('SignupForm.TitleARIA')}
                    type="text"
                    id="username"
                    autoComplete="username"
                    autoCapitalize="none"
                    {...field.input}
                  />
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </div>
              )}
            </Field>
            <Field name="email">
              {(field) => (
                <div className="form__field">
                  <label htmlFor="email" className="form__label">
                    {t('SignupForm.Email')}
                  </label>
                  <input
                    className="form__input"
                    aria-label={t('SignupForm.EmailARIA')}
                    type="email"
                    id="email"
                    autoComplete="email"
                    {...field.input}
                  />
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </div>
              )}
            </Field>
            <Field name="password">
              {(field) => (
                <div className="form__field">
                  <label htmlFor="password" className="form__label">
                    {t('SignupForm.Password')}
                  </label>
                  <div className="form__field__password">
                    <input
                      className="form__input"
                      aria-label={t('SignupForm.PasswordARIA')}
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="new-password"
                      {...field.input}
                    />
                    <button
                      className="form__eye__icon"
                      type="button"
                      onClick={handleVisibility}
                      aria-label={t(
                        showPassword
                          ? 'SignupForm.HidePasswordARIA'
                          : 'SignupForm.ShowPasswordARIA'
                      )}
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible />
                      ) : (
                        <AiOutlineEye />
                      )}
                    </button>
                  </div>
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </div>
              )}
            </Field>
            <Field name="confirmPassword">
              {(field) => (
                <div className="form__field">
                  <label htmlFor="confirmPassword" className="form__label">
                    {t('SignupForm.ConfirmPassword')}
                  </label>
                  <div className="form__field__password">
                    <input
                      className="form__input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      aria-label={t('SignupForm.ConfirmPasswordARIA')}
                      id="confirmPassword" // Match the id with htmlFor
                      autoComplete="new-password"
                      {...field.input}
                    />
                    <button
                      className="form__eye__icon"
                      type="button"
                      onClick={handleConfirmVisibility}
                      aria-label={t(
                        showConfirmPassword
                          ? 'SignupForm.HideConfirmPasswordARIA'
                          : 'SignupForm.ShowConfirmPasswordARIA'
                      )}
                    >
                      {showConfirmPassword ? (
                        <AiOutlineEyeInvisible />
                      ) : (
                        <AiOutlineEye />
                      )}
                    </button>
                  </div>
                  {field.meta.touched && field.meta.error && (
                    <span className="form-error" aria-live="polite">
                      {field.meta.error}
                    </span>
                  )}
                </div>
              )}
            </Field>
            {submitError && !modifiedSinceLastSubmit && (
              <span className="form-error" aria-live="assertive">
                {t(submitError)}
              </span>
            )}
            <Button
              type={ButtonTypes.SUBMIT}
              disabled={submitting || invalid || pristine}
            >
              {t('SignupForm.SubmitSignup')}
            </Button>
          </form>
        );
      }}
    </Form>
  );
}
